import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Seo from '../components/Seo';

/**
 * Impressum / Aviso Legal (rechtliche Anbieterkennzeichnung).
 *
 * Die Praxis hat ihren Sitz in Spanien; rechtlicher Rahmen ist daher in erster
 * Linie die Ley 34/2002 (LSSI-CE, Art. 10 – "Aviso Legal"), ergänzt um die für
 * den deutschsprachigen Markt üblichen Angaben. Inhalt liegt sprachweise inline
 * vor (kein fragiler Übersetzungs-Lookup), sodass die Seite nie crasht und beim
 * Prerendering vollständigen Text liefert.
 *
 * WICHTIG – vor Produktivschaltung ausfüllen: Für ein vollständiges Aviso Legal
 * einer Zahnarztpraxis sind in Spanien zusätzlich die Steuernummer (NIF) sowie
 * die berufsrechtlichen Angaben (Zahnärztekammer + Kollegiatsnummer) Pflicht.
 * Diese Werte unten eintragen – leere Felder werden automatisch ausgeblendet.
 */

// ── Stammdaten ────────────────────────────────────────────────────────────────
const PROVIDER = 'SeaDent Mallorca';
const OWNER = 'Jan D. Heiringhoff';
const STREET = 'Avenida de Jaime III, № 3';
const CITY = '07012 Palma de Mallorca, Illes Balears, España';
const PHONE = '+34 603 31 96 48';
const EMAIL = 'info@seadent.es';

// ── Noch zu ergänzende Pflichtangaben (leer = wird ausgeblendet) ───────────────
const NIF = ''; // TODO: Steuernummer (NIF/CIF) des Inhabers/der Praxis eintragen
const CHAMBER = 'Il·lustre Col·legi Oficial d’Odontòlegs i Estomatòlegs de les Illes Balears (COEB)';
const COLLEGIATE_NO = ''; // TODO: Kollegiatsnummer (Nº de colegiado) eintragen
const PROF_TITLE_COUNTRY = 'Deutschland'; // Land, in dem die Berufsbezeichnung verliehen wurde

const LAST_UPDATED = 'Juni 2026';
const ODR_URL = 'https://ec.europa.eu/consumers/odr/';

type Lang = 'de' | 'en' | 'es' | 'ru';

interface LegalContent {
  title: string;
  providerHeading: string;
  ownerLabel: string;
  contactHeading: string;
  phoneLabel: string;
  emailLabel: string;
  taxHeading: string;
  nifLabel: string;
  professionHeading: string;
  professionTitleLabel: string;
  professionTitleValue: string; // z. B. "Zahnarzt (verliehen in …)"
  chamberLabel: string;
  collegiateLabel: string;
  responsibleHeading: string;
  responsibleText: string;
  liabilityContentHeading: string;
  liabilityContentText: string;
  liabilityLinksHeading: string;
  liabilityLinksText: string;
  copyrightHeading: string;
  copyrightText: string;
  odrHeading: string;
  odrText: string;
  status: string;
}

const CONTENT: Record<Lang, LegalContent> = {
  de: {
    title: 'Impressum',
    providerHeading: 'Anbieter / Verantwortlicher',
    ownerLabel: 'Inhaber',
    contactHeading: 'Kontakt',
    phoneLabel: 'Telefon',
    emailLabel: 'E-Mail',
    taxHeading: 'Steuerliche Angaben',
    nifLabel: 'Steuernummer (NIF)',
    professionHeading: 'Berufsrechtliche Angaben',
    professionTitleLabel: 'Berufsbezeichnung',
    professionTitleValue: `Zahnarzt (verliehen in ${PROF_TITLE_COUNTRY})`,
    chamberLabel: 'Zuständige Kammer',
    collegiateLabel: 'Kollegiatsnummer',
    responsibleHeading: 'Verantwortlich für den Inhalt',
    responsibleText: `${OWNER}, Anschrift wie oben.`,
    liabilityContentHeading: 'Haftung für Inhalte',
    liabilityContentText:
      'Die Inhalte dieser Website wurden mit größtmöglicher Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Die auf dieser Website bereitgestellten Informationen stellen keine medizinische Beratung dar und ersetzen nicht die persönliche Untersuchung und Beratung durch zahnärztliches Fachpersonal.',
    liabilityLinksHeading: 'Haftung für Links',
    liabilityLinksText:
      'Unser Angebot enthält gegebenenfalls Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber verantwortlich. Bei Bekanntwerden von Rechtsverletzungen entfernen wir derartige Links umgehend.',
    copyrightHeading: 'Urheberrecht',
    copyrightText:
      'Die auf dieser Website veröffentlichten Inhalte, Texte und Bilder unterliegen dem Urheberrecht. Jede Verwertung außerhalb der gesetzlich zulässigen Grenzen bedarf der vorherigen schriftlichen Zustimmung des jeweiligen Urhebers.',
    odrHeading: 'EU-Streitschlichtung',
    odrText:
      'Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit, die Sie unter folgendem Link finden:',
    status: `Stand: ${LAST_UPDATED}`,
  },
  en: {
    title: 'Legal Notice',
    providerHeading: 'Provider / Responsible Entity',
    ownerLabel: 'Owner',
    contactHeading: 'Contact',
    phoneLabel: 'Phone',
    emailLabel: 'Email',
    taxHeading: 'Tax Information',
    nifLabel: 'Tax Number (NIF)',
    professionHeading: 'Professional Information',
    professionTitleLabel: 'Professional Title',
    professionTitleValue: `Dentist (awarded in ${PROF_TITLE_COUNTRY})`,
    chamberLabel: 'Competent Chamber',
    collegiateLabel: 'Membership Number',
    responsibleHeading: 'Responsible for Content',
    responsibleText: `${OWNER}, address as above.`,
    liabilityContentHeading: 'Liability for Content',
    liabilityContentText:
      'The contents of this website have been prepared with the greatest possible care. However, we cannot guarantee the accuracy, completeness, or timeliness of the content. The information provided on this website does not constitute medical advice and does not replace a personal examination and consultation by qualified dental professionals.',
    liabilityLinksHeading: 'Liability for Links',
    liabilityLinksText:
      'Our website may contain links to external third-party websites over whose content we have no influence. The respective provider or operator of the linked pages is always responsible for their content. We will remove such links immediately if we become aware of any legal violations.',
    copyrightHeading: 'Copyright',
    copyrightText:
      'The content, texts, and images published on this website are protected by copyright. Any use beyond the limits permitted by law requires the prior written consent of the respective author.',
    odrHeading: 'EU Dispute Resolution',
    odrText:
      'The European Commission provides a platform for online dispute resolution (ODR), which you can find at the following link:',
    status: `Last updated: ${LAST_UPDATED}`,
  },
  es: {
    title: 'Aviso Legal',
    providerHeading: 'Titular / Responsable',
    ownerLabel: 'Titular',
    contactHeading: 'Contacto',
    phoneLabel: 'Teléfono',
    emailLabel: 'Correo electrónico',
    taxHeading: 'Datos fiscales',
    nifLabel: 'Número de identificación fiscal (NIF)',
    professionHeading: 'Información profesional',
    professionTitleLabel: 'Título profesional',
    professionTitleValue: `Odontólogo (título obtenido en ${PROF_TITLE_COUNTRY})`,
    chamberLabel: 'Colegio profesional',
    collegiateLabel: 'Número de colegiado',
    responsibleHeading: 'Responsable del contenido',
    responsibleText: `${OWNER}, dirección indicada anteriormente.`,
    liabilityContentHeading: 'Responsabilidad sobre los contenidos',
    liabilityContentText:
      'Los contenidos de este sitio web se han elaborado con el mayor cuidado posible. No obstante, no podemos garantizar la exactitud, integridad ni actualidad de los contenidos. La información facilitada en este sitio web no constituye asesoramiento médico y no sustituye el examen ni la consulta personal con profesionales odontológicos cualificados.',
    liabilityLinksHeading: 'Responsabilidad sobre los enlaces',
    liabilityLinksText:
      'Nuestro sitio web puede contener enlaces a sitios web externos de terceros sobre cuyo contenido no tenemos influencia. El proveedor u operador de las páginas enlazadas es siempre responsable de sus contenidos. Eliminaremos dichos enlaces de inmediato si tenemos conocimiento de cualquier infracción legal.',
    copyrightHeading: 'Derechos de autor',
    copyrightText:
      'Los contenidos, textos e imágenes publicados en este sitio web están protegidos por derechos de autor. Cualquier uso que exceda los límites permitidos por la ley requiere el consentimiento previo y por escrito del autor correspondiente.',
    odrHeading: 'Resolución de litigios de la UE',
    odrText:
      'La Comisión Europea facilita una plataforma de resolución de litigios en línea (RLL) que puede encontrar en el siguiente enlace:',
    status: `Última actualización: ${LAST_UPDATED}`,
  },
  ru: {
    title: 'Правовая информация',
    providerHeading: 'Поставщик / Ответственное лицо',
    ownerLabel: 'Владелец',
    contactHeading: 'Контакты',
    phoneLabel: 'Телефон',
    emailLabel: 'Эл. почта',
    taxHeading: 'Налоговые данные',
    nifLabel: 'Налоговый номер (NIF)',
    professionHeading: 'Профессиональная информация',
    professionTitleLabel: 'Профессиональное звание',
    professionTitleValue: `Стоматолог (звание получено в ${PROF_TITLE_COUNTRY})`,
    chamberLabel: 'Профессиональная палата',
    collegiateLabel: 'Регистрационный номер',
    responsibleHeading: 'Ответственный за содержание',
    responsibleText: `${OWNER}, адрес указан выше.`,
    liabilityContentHeading: 'Ответственность за содержание',
    liabilityContentText:
      'Содержание этого веб-сайта подготовлено с максимальной тщательностью. Тем не менее мы не можем гарантировать точность, полноту и актуальность содержания. Информация на этом сайте не является медицинской консультацией и не заменяет личного осмотра и консультации квалифицированного стоматолога.',
    liabilityLinksHeading: 'Ответственность за ссылки',
    liabilityLinksText:
      'Наш сайт может содержать ссылки на внешние сайты третьих лиц, на содержание которых мы не имеем влияния. За содержание сторонних страниц всегда несёт ответственность их поставщик или оператор. При выявлении нарушений мы незамедлительно удалим такие ссылки.',
    copyrightHeading: 'Авторское право',
    copyrightText:
      'Содержание, тексты и изображения, опубликованные на этом сайте, защищены авторским правом. Любое использование за пределами, разрешёнными законом, требует предварительного письменного согласия соответствующего автора.',
    odrHeading: 'Разрешение споров в ЕС',
    odrText:
      'Европейская комиссия предоставляет платформу для онлайн-урегулирования споров (ОУС), которую вы можете найти по следующей ссылке:',
    status: `Последнее обновление: ${LAST_UPDATED}`,
  },
};

const LegalNoticePage: React.FC = () => {
  const { language } = useLanguage();
  const t = CONTENT[(language as Lang)] ?? CONTENT.de;

  return (
    <div className="container mx-auto px-4 pt-20 lg:pt-32 pb-8 max-w-4xl">
      <Seo pageKey="legal" />
      <h1 className="text-3xl font-bold text-center mb-10">{t.title}</h1>

      <div className="prose max-w-none">
        {/* Anbieter */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">{t.providerHeading}</h2>
          <p className="mb-1 font-medium">{PROVIDER}</p>
          <p className="mb-1">{t.ownerLabel}: {OWNER}</p>
          <p className="mb-1">{STREET}</p>
          <p className="mb-1">{CITY}</p>
        </section>

        {/* Kontakt */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">{t.contactHeading}</h2>
          <p className="mb-1">{t.phoneLabel}: {PHONE}</p>
          <p className="mb-1">{t.emailLabel}: {EMAIL}</p>
        </section>

        {/* Berufsrechtliche Angaben */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">{t.professionHeading}</h2>
          <p className="mb-1">{t.professionTitleLabel}: {t.professionTitleValue}</p>
          {CHAMBER && <p className="mb-1">{t.chamberLabel}: {CHAMBER}</p>}
          {COLLEGIATE_NO && <p className="mb-1">{t.collegiateLabel}: {COLLEGIATE_NO}</p>}
        </section>

        {/* Steuerliche Angaben (nur wenn NIF gesetzt) */}
        {NIF && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-3">{t.taxHeading}</h2>
            <p className="mb-1">{t.nifLabel}: {NIF}</p>
          </section>
        )}

        {/* Verantwortlich für den Inhalt */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">{t.responsibleHeading}</h2>
          <p className="mb-1">{t.responsibleText}</p>
        </section>

        {/* Haftung für Inhalte */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">{t.liabilityContentHeading}</h2>
          <p className="mb-1">{t.liabilityContentText}</p>
        </section>

        {/* Haftung für Links */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">{t.liabilityLinksHeading}</h2>
          <p className="mb-1">{t.liabilityLinksText}</p>
        </section>

        {/* Urheberrecht */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">{t.copyrightHeading}</h2>
          <p className="mb-1">{t.copyrightText}</p>
        </section>

        {/* EU-Streitschlichtung */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">{t.odrHeading}</h2>
          <p className="mb-1">
            {t.odrText}{' '}
            <a href={ODR_URL} target="_blank" rel="noopener noreferrer" className="text-teal underline">
              {ODR_URL}
            </a>
          </p>
        </section>

        <p className="text-sm text-gray-500 mt-10">{t.status}</p>
      </div>
    </div>
  );
};

export default LegalNoticePage;
